import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Loader2, Save, RotateCcw, ShieldAlert } from 'lucide-react';

const FIELDS = [
  { k: 'dpr_pct', l: 'Class delivery', hint: 'Classes held out of classes held + cancelled without an approved reason' },
  { k: 'syllabus_pct', l: 'Syllabus completion', hint: 'Topics ticked off against the course syllabus' },
  { k: 'attendance_pct', l: 'Student attendance', hint: 'Present + late share of the marks the trainer submitted' },
  { k: 'conversion_pct', l: 'Demo conversion', hint: 'Demos that turned into an admission' },
  { k: 'collection_pct', l: 'Collection against billing', hint: 'Money collected against what was billed to their students' },
];

const WFIELDS = [
  { k: 'business_value', block: 'Business' },
  { k: 'collection', block: 'Business' },
  { k: 'dpr', block: 'Delivery' },
  { k: 'syllabus', block: 'Delivery' },
  { k: 'attendance', block: 'Delivery' },
  { k: 'conversion', block: 'Demos' },
];
const BLOCK_CLS = { Business: 'text-emerald-700', Delivery: 'text-violet-700', Demos: 'text-sky-700' };
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Admin-set targets (verdicts) and index weights (the score itself). */
export default function PerformanceTargetsDialog({ open, isAdmin, onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [vals, setVals] = useState({});
  const [wts, setWts] = useState({});
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!open) return;
    setConfirm('');
    api.get('/training/trainer-performance/config')
      .then((r) => { setCfg(r.data); setVals(r.data.targets); setWts(r.data.weights); })
      .catch((e) => { toast.error(e.response?.data?.detail || 'Could not load the configuration'); onClose?.(); });
  }, [open, onClose]);

  const save = async () => {
    setBusy('targets');
    try {
      const r = await api.put('/training/trainer-performance/config', { targets: vals });
      setVals(r.data.targets);
      toast.success('Targets saved — the verdicts update straight away');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  const total = Math.round(WFIELDS.reduce((a, f) => a + num(wts[f.k]), 0) * 10) / 10;
  const saveWeights = async () => {
    setBusy('weights');
    try {
      const r = await api.put('/training/trainer-performance/weights', { weights: wts, confirm });
      setWts(r.data.weights);
      setConfirm('');
      toast.success('Weights changed — every index has been re-scored');
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change the weights'); }
    finally { setBusy(''); }
  };

  const example = (() => {
    const t = vals.dpr_pct ?? 90;
    return `A trainer who held 18 classes and cancelled 2 without an approved reason sits at 90% class delivery. With the target at ${t}%, that reads as ${90 >= t ? '“working well”' : '“needs improvement”'}.`;
  })();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-white" data-testid="perf-targets-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Review targets &amp; index weights</DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">
            <b>Targets</b> decide what shows up as working well versus needs improvement on a trainer's sheet.
            <b> Weights</b> decide how the 0–100 index itself is built. Both are admin-only.
          </DialogDescription>
        </DialogHeader>

        {!cfg ? (
          <div className="flex items-center justify-center py-14 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
        ) : (
          <div className="space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Targets</p>
            {FIELDS.map((f) => (
              <div key={f.k} className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-[12.5px] font-semibold text-slate-800">{f.l}</p>
                  <p className="text-[11px] text-slate-500">{f.hint}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input type="number" min={0} max={100} value={vals[f.k] ?? ''} disabled={!isAdmin}
                    onChange={(e) => setVals((p) => ({ ...p, [f.k]: e.target.value }))}
                    className="h-9 w-20 text-right text-[13px]" data-testid={`target-${f.k}`} />
                  <span className="text-[12px] text-slate-500">%</span>
                </div>
                <span className="text-[10.5px] text-slate-400 w-20 text-right">default {cfg.defaults[f.k]}%</span>
              </div>
            ))}
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setVals(cfg.defaults)} data-testid="target-reset">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Back to defaults
                </Button>
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={save} disabled={busy === 'targets'} data-testid="target-save">
                  {busy === 'targets' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Save targets
                </Button>
              </div>
            )}

            {/* ── index weights ── */}
            <Card className="p-4 border border-amber-200 bg-amber-50/50 space-y-3" data-testid="weights-editor">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-700" />
                <p className="text-[11px] font-bold uppercase tracking-wider text-amber-800">Index weights — “How the score was built”</p>
                <span className={`ml-auto px-2 py-0.5 rounded-full text-[11px] font-bold border ${
                  total === 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}
                  data-testid="weights-total">{total} / 100</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {WFIELDS.map((f) => (
                  <div key={f.k} className="flex items-center gap-2 bg-white rounded border border-slate-200 px-2.5 py-1.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-slate-800 truncate">{cfg.labels[f.k]}</p>
                      <p className={`text-[10.5px] ${BLOCK_CLS[f.block]}`}>{f.block} · default {cfg.weight_defaults[f.k]}</p>
                    </div>
                    <Input type="number" min={0} max={100} step="0.5" value={wts[f.k] ?? ''} disabled={!isAdmin}
                      onChange={(e) => setWts((p) => ({ ...p, [f.k]: e.target.value }))}
                      className="h-8 w-16 text-right text-[13px]" data-testid={`weight-${f.k}`} />
                  </div>
                ))}
              </div>
              <p className="text-[11.5px] text-slate-600">
                Weights must add up to <b>100</b>. Changing them re-scores every trainer for every period straight away,
                so type <b>CONFIRM</b> below to prove you meant it.
              </p>
              {isAdmin && (
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type CONFIRM"
                    className="h-9 w-40 text-[13px] bg-white" data-testid="weights-confirm" />
                  <Button size="sm" className="h-9 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={saveWeights} disabled={busy === 'weights' || total !== 100 || confirm.trim() !== 'CONFIRM'}
                    data-testid="weights-save">
                    {busy === 'weights' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Change the weights
                  </Button>
                  <Button size="sm" variant="outline" className="h-9" onClick={() => setWts(cfg.weight_defaults)} data-testid="weights-reset">
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Default weights
                  </Button>
                </div>
              )}
            </Card>

            <Card className="p-3.5 border bg-slate-50/80 space-y-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">How the numbers work</p>
              <p className="text-[12px] text-slate-600">{example}</p>
              <p className="text-[12px] text-slate-600">
                <b>Worked example (current weights):</b> the trainer billing the most business in the window earns the full{' '}
                {num(wts.business_value)}. If they collect 95% of it they earn {Math.round(num(wts.collection) * 0.95 * 10) / 10} of{' '}
                {num(wts.collection)}. At 75% class delivery they earn {Math.round(num(wts.dpr) * 0.75 * 10) / 10} of {num(wts.dpr)}, and with
                no syllabus data that {num(wts.syllabus)} drops out and the rest re-balance — which is how a partial score still
                lands on a fair 0–100 index and a band.
              </p>
              <p className="text-[12px] text-slate-600">
                <b>Bands:</b> {(cfg.bands || []).map((b) => `${b.band} ≥ ${b.from}`).join(' · ')}.
              </p>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose?.()} data-testid="target-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
